// 🆕 [Phase 56.4b] Composant extrait depuis CalendrierProduction.jsx — UNE rangée semaine
// de la grille calendaire (header dates + lanes-chefs + lane lavage en bas).
//
// Consomme le state partagé via CalendarContext (cf. CalendarContext.jsx).
// Préserve scrupuleusement le rendu legacy (1:1 du code original lignes ~1505-2314).
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  OctagonX, AlertTriangle, User, ExternalLink, CalendarIcon
} from 'lucide-react';
import { formatNacelleDisplay } from '@/lib/formatNacelle';
import { computeBadgesParCellule, ENABLE_PHASE51_ADDENDUM } from '@/lib/calendarPhase51';
import { getCCQInfo, isJourFerieCCQ, isCongeCCQ } from '@/lib/ccq-holidays';
import { useCalendar } from './CalendarContext';
// 🆕 [Phase 54 D.3] Branchement conditionnel vers le rendu CSS Grid
import GanttGridRow from './GanttGridCalendrier';

// Helper local pour les emojis météo (identique à celui du parent)
const weatherEmoji = (code) => {
  if (code === 0) return '☀️';
  if (code <= 3) return '⛅';
  if (code <= 48) return '🌫️';
  if (code <= 67) return '🌧️';
  if (code <= 77) return '❄️';
  if (code <= 82) return '🌧️';
  if (code >= 95) return '⛈️';
  return '☁️';
};

function CalendarWeekRow({ week, weekIdx, weekBons }) {
  const {
    bons, employees, currentDate, routesLavage,
    congesParDate, noJobWorkers, unorderedByDate,
    agendaEvents, meteo, punchesCache, dossierCache,
    showMeteo, showConges, showAgenda,
    dragging, hoveredBon,
    setHoveredBon, setDragging, setRdvPopup,
    setShowServiceCallPunch, setOrdreDialog, setShowNoJobPanel,
    handleBarClick, handleDayDoubleClick, handleExtendBon, openEditDatesDialog,
    fetchDossierInfo, fetchPunches,
    toDateStr, navigate, presentsParJour,
  } = useCalendar();

  const isCurrentMonth = week.some(d => d.getMonth() === currentDate.getMonth());
  if (!isCurrentMonth && weekIdx > 0) return null;

  return (
    <div key={weekIdx} className="border-t flex flex-col"
      style={{ minHeight: '110px', transition: 'min-height 0.25s ease-out' }}
      onDoubleClick={(e) => {
        // Ignorer si on a double-cliqué sur un BT (la bande intercepte le clic)
        if (e.target.closest('[data-testid^="gantt-bar-"]')) return;
        // Calculer le jour à partir de la position X
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const dayIdx = Math.floor((x / rect.width) * 7);
        if (dayIdx < 0 || dayIdx > 6) return;
        const day = week[dayIdx];
        if (!day || day.getMonth() !== currentDate.getMonth()) return;
        if (day.getDay() === 0 || day.getDay() === 6) return;
        handleDayDoubleClick(toDateStr(day));
      }}
    >
      {/* Date numbers + météo + CCQ indicators */}
      <div className="grid grid-cols-7">
        {week.map((day, dayIdx) => {
          const isThisMonth = day.getMonth() === currentDate.getMonth();
          const isToday = toDateStr(day) === toDateStr(new Date());
          const dayMeteo = meteo[toDateStr(day)];
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const ccqInfo = getCCQInfo(toDateStr(day));
          const hasCCQ = ccqInfo.jourFerie || ccqInfo.conge;
          const isRainAlert = dayMeteo && dayMeteo.precip_pct >= 55;
          // ⚠ No-job workers pour ce jour
          const noJobThisDay = noJobWorkers[toDateStr(day)] || [];

          // 🚀 Phase 3.5 perf fix — Lecture O(1) depuis map précalculée useMemo
          const unorderedByChef = (isThisMonth && !isWeekend)
            ? (unorderedByDate[toDateStr(day)] || [])
            : [];

          return (
            <div 
              key={dayIdx} 
              className={`px-2 py-1 text-sm flex justify-between items-center border-r border-b cursor-pointer ${!isThisMonth ? 'text-gray-300 bg-white' : isToday ? 'bg-blue-100 text-blue-700 font-bold ring-2 ring-blue-400 ring-inset rounded-sm' : isWeekend ? 'text-gray-400 bg-gray-100' : 'text-gray-700 bg-white'} ${hasCCQ && isThisMonth ? 'relative' : ''}`}
              title={ccqInfo.jourFerie ? ccqInfo.jourFerie.nom : ccqInfo.conge ? ccqInfo.conge.nom : (noJobThisDay.length > 0 ? `⚠ ${noJobThisDay.map(e => e.employee_nom).join(', ')} sans job` : '')}
              onDoubleClick={() => {
                if (!isThisMonth || isWeekend) return;
                handleDayDoubleClick(toDateStr(day));
              }}
            >
              {/* Indicateur CCQ */}
              {hasCCQ && isThisMonth && (
                <div className="absolute inset-0 pointer-events-none">
                  {ccqInfo.jourFerie && (
                    <div className="absolute inset-0 bg-red-200/80" />
                  )}
                  {ccqInfo.conge && !ccqInfo.jourFerie && (
                    <div className="absolute inset-0 bg-yellow-200/70" />
                  )}
                </div>
              )}
              <div className="flex-1 relative z-10">
                {hasCCQ && isThisMonth && (
                  <span className={`text-[8px] font-bold leading-tight block truncate ${ccqInfo.jourFerie ? 'text-red-700' : 'text-yellow-700'}`}>
                    {ccqInfo.jourFerie?.nom || 'Congé CCQ'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 relative z-10">
                {/* Indicateur congés employés */}
                {showConges && isThisMonth && !isWeekend && congesParDate[toDateStr(day)]?.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="flex items-center gap-0.5 text-[9px] bg-violet-100 hover:bg-violet-200 text-violet-700 px-1 py-0.5 rounded font-semibold transition-colors shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        title="Employés en congé"
                        data-testid={`conge-indicator-${toDateStr(day)}`}
                      >
                        <OctagonX className="w-3 h-3 text-red-600" /> {congesParDate[toDateStr(day)].length}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="start" className="w-52 p-2 z-[200]" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs font-semibold text-violet-800 mb-1.5 flex items-center gap-1">
                        <OctagonX className="w-3 h-3 text-red-600" /> En congé ce jour ({congesParDate[toDateStr(day)].length})
                      </p>
                      <div className="space-y-0.5">
                        {congesParDate[toDateStr(day)].map((emp, i) => (
                          <p key={emp.id || emp._id || i} className="text-xs text-amber-700">• {emp.nom}</p>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {showMeteo && dayMeteo && (
                  <span
                    className={`text-sm flex items-center gap-0.5 min-w-0 overflow-hidden cursor-help`}
                    title={`Météo: ${weatherEmoji(dayMeteo.code)} ${Math.round(dayMeteo.temp_min)}°–${Math.round(dayMeteo.temp_max)}° | Pluie: ${dayMeteo.precip_pct}% | Vent: ${Math.round(dayMeteo.vent_max || 0)} km/h`}
                  >
                    <span className="flex-shrink-0 text-base">{weatherEmoji(dayMeteo.code)}</span>
                    <span className="text-sm text-gray-600 tabular-nums font-medium">{Math.round(dayMeteo.temp_max)}°</span>
                    {dayMeteo.precip_pct > 0 && (
                      <span className={`text-xs tabular-nums font-semibold ${dayMeteo.precip_pct >= 55 ? 'text-orange-500' : 'text-blue-400'}`}>{dayMeteo.precip_pct}%</span>
                    )}
                  </span>
                )}
                <span className="font-medium">{day.getDate()}</span>
                {/* ⚠ No-job workers indicator */}
                {noJobThisDay.length > 0 && isThisMonth && (
                  <span
                    className="flex items-center gap-0.5 text-amber-600 cursor-pointer hover:scale-110 transition-transform"
                    title={`⚠ Sans job : ${noJobThisDay.map(e => e.employee_nom).join(', ')}`}
                    onClick={(e) => { e.stopPropagation(); setShowNoJobPanel(true); }}
                  >
                    <span className="text-xs font-bold">⚠</span>
                    <span className="text-[10px] font-bold">{noJobThisDay.length}</span>
                  </span>
                )}
                {/* 🆕 Phase 3.5 — Icône ⚠️ BT non ordonnés */}
                {unorderedByChef.length > 0 && (
                  <span
                    className="flex items-center gap-0.5 text-red-600 cursor-pointer hover:scale-110 transition-transform ml-1"
                    title={`⚠️ ${unorderedByChef.map(c => `${c.chefNom} (${c.count} BT)`).join(', ')} — BT non ordonnés — cliquer pour ordonner`}
                    data-testid={`btn-ordonner-day-${toDateStr(day)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const first = unorderedByChef[0];
                      setOrdreDialog({ chefId: first.chefId, chefNom: first.chefNom, date: toDateStr(day) });
                    }}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    <span className="text-[10px] font-bold">{unorderedByChef.length}</span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Gantt bars — LANES FIXES par équipe */}
      {/* 🆕 [Tâche 6 SESSION MAX 2026-06-09] CSS Grid actif en prod, branche legacy supprimée */}
      {/* Nouveau rendu CSS Grid — Phase 54 D.4f : feature parité finale !
         RDV row au-dessus de chaque lane chef + Lavage row en bas du week.
         Double-click insertion BT déjà géré par le wrapper parent (L50-62). */}
      <GanttGridRow
        bons={weekBons}
        weekStartDate={toDateStr(week[0])}
        presentsParJour={presentsParJour}
        onBonClick={(bon, cellDate) => handleBarClick(bon, cellDate)}
        allBonsThisWeek={weekBons}
        allBons={bons}
        employees={employees}
        dossierCache={dossierCache}
        punchesCache={punchesCache}
        navigate={navigate}
        openEditDatesDialog={openEditDatesDialog}
        dragging={dragging}
        setDragging={setDragging}
        handleExtendBon={handleExtendBon}
        setShowServiceCallPunch={setShowServiceCallPunch}
        agendaEvents={agendaEvents}
        showAgenda={showAgenda}
        setRdvPopup={setRdvPopup}
        routesLavage={routesLavage}
      />
      {/* 🐛 [Hotfix v13.36 — 2026-07-09] Bug #1 : rangée LAVAGE inline supprimée.
          Doublon d'affichage causé par le refactor Phase 54 D.4f qui a extrait
          <GanttLavageLane> dans <GanttGridRow> ci-dessus mais n'avait pas
          nettoyé le rendu inline ici. Résultat : chaque route affichée 2× dans
          le calendrier. GanttLavageLane (rendue par GanttGridRow via
          routesLavage={routesLavage}) reste la source de vérité unique. */}
    </div>
  );
}

export default CalendarWeekRow;
